/**
 * AdditionalQueryColumnsCanvas — podgląd klocka „Widok SQL" na płótnie kreatora formatek.
 *
 * Odpowiednik `AdditionalTableColumnsCanvas` dla widoków SQL. Gdy host kreatora ma dostęp do
 * danych tenanta (`useBuilderEnvironment().tenantData`) i klocek ma wybrany widok, płótno pokazuje
 * EDYTOR UKŁADU KOLUMN (`TableLayoutEditor`): nagłówki kolumn wyniku widoku z jednym wierszem
 * przykładowym, a przy możliwości zapisu — przeciąganie, szerokość, menu kolumny i panel
 * „Kolumny". Każda akcja wysyła jedną łatkę `settings` (`tableColumns` / `hiddenColumnIds`)
 * przez `onSettingsChange`. Id kolumn liczy `additionalViewColumnIds` (powtórzona nazwa kolumny
 * → `nazwa#2`), tę samą funkcję zastosuje karta rekordu.
 *
 * Kolumny widoku są znane dopiero po uruchomieniu zapytania, więc model
 * (`useAdditionalViewColumnsModel`) wykonuje je po stronie serwera. Gdy baza odrzuci zapytanie,
 * placeholder pokazuje POWÓD z serwera (np. „division by zero") — kreator budują tylko
 * administratorzy.
 *
 * Układ systemowy (`systemHideOnly`) przełącza edytor w tryb `hideOnly`: sam panel „Kolumny" z
 * checkboxami, a akcja wysyła wyłącznie `{ hiddenColumnIds }`. Kreator wyprowadza z tej maski
 * nakładkę tenanta (`tab_overrides.elementOverrides`) kluczowaną WIDOKIEM
 * (`additional_query:<viewSlug>`), więc kilka widoków SQL na jednym układzie się nie zderza.
 *
 * W pozostałych przypadkach (brak providera środowiska — np. projektant karty kanban, kreator
 * modułów własnych; kreator osadzony w panelu ITE, gdzie endpointy tenanta odpowiadają 401;
 * widok jeszcze niewybrany, niezaładowany, nieistniejący albo z błędnym zapytaniem) płótno rysuje
 * dotychczasowy przerywany placeholder, a przy błędzie lub braku widoku podaje powód. Bez
 * `onSettingsChange` (podgląd tylko do odczytu) edytor jest podglądem bez kontrolek.
 *
 * Zapytania (katalog widoków, kolumny widoku) żyją WYŁĄCZNIE w komponencie wewnętrznym —
 * montowanym dopiero po przejściu bramki, więc host bez dostępu nie wysyła ich wcale.
 *
 * Korzeń (`className`/`style`): `LayoutElementRegistry.applyElementStyle` klonuje zwrócony element
 * i dokłada styl elementu, więc oba warianty (edytor i placeholder) przekazują te propsy na swój
 * korzeń DOM.
 */

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Database } from 'lucide-react';

import { TableLayoutEditor } from '@/components/embedded-table/TableLayoutEditor';
import { useBuilderEnvironment } from '@/components/fields/builder-environment';
import type { LayoutElement } from '@/components/fields/LayoutElementRegistry';
import { useAdditionalViewColumnsModel } from '@/lib/embedded-table/use-additional-view-columns-model';
import { cn } from '@/lib/utils';

interface PlaceholderProps {
  elementId: string;
  /** Kolumny wybranego widoku jeszcze się ładują — placeholder dopisuje krótką informację. */
  loading?: boolean;
  /** Powód, dla którego nie ma edytora (błąd zapytania, brak widoku) — krótki tekst pod nazwą klocka. */
  message?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Przerywana ramka z ikoną i nazwą klocka — wspólny wygląd klocka tam, gdzie nie ma
 * edytora kolumn (płótno bez dostępu do danych tenanta, podgląd poza kreatorem).
 */
export function AdditionalQueryViewPlaceholder({
  elementId,
  loading = false,
  message,
  className,
  style,
}: PlaceholderProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center',
        className,
      )}
      style={style}
      data-testid={`additional-query-view-element-${elementId}`}
      data-element-type="additional_query_view"
    >
      <Database className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
      <p className="mt-1 text-xs font-medium text-text-secondary">
        {t('additionalViews.paletteLabel', { defaultValue: 'Widok SQL' })}
      </p>
      {loading && <p className="mt-1 text-xs text-text-muted">{t('common.loading')}</p>}
      {message !== undefined && (
        <p
          className="mt-1 text-xs text-text-muted"
          data-testid={`additional-query-view-message-${elementId}`}
        >
          {message}
        </p>
      )}
    </div>
  );
}

interface CanvasProps {
  element: LayoutElement;
  /** Zapis łatki `settings` elementu; brak = podgląd tylko do odczytu. */
  onSettingsChange?: (patch: Record<string, unknown>) => void;
  /**
   * Układ systemowy — edytor w trybie `hideOnly` (tenant tylko ukrywa kolumny; łatka
   * `{ hiddenColumnIds }`). Bez `onSettingsChange` i tak zostaje podglądem bez kontrolek.
   */
  systemHideOnly?: boolean;
  className?: string;
  style?: CSSProperties;
}

interface EditorProps extends CanvasProps {
  viewSlug: string;
}

/** Edytor kolumn wybranego widoku — jedyne miejsce z zapytaniami o dane tenanta. */
function AdditionalQueryColumnsEditor({
  element,
  viewSlug,
  onSettingsChange,
  systemHideOnly = false,
  className,
  style,
}: EditorProps) {
  const { t } = useTranslation();
  const model = useAdditionalViewColumnsModel(viewSlug);

  if (model.status !== 'ready') {
    const message =
      model.status === 'error'
        ? model.errorReason !== undefined
          ? t('additionalViews.columnsLoadError', { reason: model.errorReason })
          : t('additionalViews.columnsLoadErrorNoReason')
        : model.status === 'notFound'
          ? t('additionalViews.grid.viewNotFound', { slug: viewSlug })
          : undefined;
    return (
      <AdditionalQueryViewPlaceholder
        elementId={element.id}
        loading={model.status === 'pending'}
        message={message}
        className={className}
        style={style}
      />
    );
  }

  return (
    <TableLayoutEditor
      descriptors={model.descriptors}
      settings={element.settings}
      onChange={onSettingsChange}
      // Układ systemowy: tenant tylko ukrywa kolumny (maska `hiddenColumnIds`, patrz nagłówek pliku).
      mode={systemHideOnly ? 'hideOnly' : 'full'}
      title={model.title ?? viewSlug}
      // `min-w-0` — szeroki widok przewija się w karcie (pasek nagłówków ma `overflow-x-auto`),
      // zamiast rozpychać rodzica.
      className={cn('min-w-0', className)}
      style={style}
      testIdPrefix={`additional-query-view-element-${element.id}`}
    />
  );
}

export function AdditionalQueryColumnsCanvas({
  element,
  onSettingsChange,
  systemHideOnly = false,
  className,
  style,
}: CanvasProps) {
  const { tenantData } = useBuilderEnvironment();
  const rawSlug = element.settings?.viewSlug;
  const viewSlug = typeof rawSlug === 'string' ? rawSlug : '';

  if (!tenantData || viewSlug === '') {
    return (
      <AdditionalQueryViewPlaceholder elementId={element.id} className={className} style={style} />
    );
  }

  return (
    <AdditionalQueryColumnsEditor
      element={element}
      viewSlug={viewSlug}
      onSettingsChange={onSettingsChange}
      systemHideOnly={systemHideOnly}
      className={className}
      style={style}
    />
  );
}
